/**
 * Finding a document's font on the user's own computer.
 *
 * A PDF made in Word embeds only the letters it used. When an edit needs one
 * it left out, the faithful source is the same font on the machine the
 * document came from -- and City PCs have Calibri, while the Linux server
 * legitimately cannot. The browser's Local Font Access API hands Odie that
 * font after a one-time permission prompt; the server keeps it in memory for
 * the session only.
 *
 * Matching mirrors backend/odie/pdf/fonts/names.py: PostScript name first,
 * then family and style, never "something similar".
 */

export interface LocalFontWanted {
  postscriptNames: string[]
  /** Normalised, as the server sends it: "calibri". */
  family: string
  /** Normalised: "" for regular, "bold", "bold italic". */
  style: string
}

export const localFontsSupported = (): boolean =>
  typeof window !== 'undefined' && typeof window.queryLocalFonts === 'function'

/**
 * The font new text is set in when this computer has it -- added text,
 * comments, stamps, page marks and the OCR layer. City PCs have Calibri, the
 * font the documents staff make are set in; the server may only carry an
 * open-licence stand-in.
 */
export const DEFAULT_LOCAL_FONT: LocalFontWanted = {
  postscriptNames: ['Calibri'],
  family: 'calibri',
  style: '',
}

/**
 * Whether the browser will hand over local fonts: "granted" works without
 * asking, "prompt" needs a user gesture (a click) to ask.
 */
export async function localFontPermission(): Promise<PermissionState | 'unsupported'> {
  if (!localFontsSupported()) return 'unsupported'
  try {
    const status = await navigator.permissions.query({ name: 'local-fonts' as PermissionName })
    return status.state
  } catch {
    return 'prompt'
  }
}

let defaultFont: Promise<Blob | null> | null = null

/** DEFAULT_LOCAL_FONT from this computer, read once per page load. */
export function findDefaultLocalFont(): Promise<Blob | null> {
  if (!defaultFont) {
    defaultFont = findLocalFont(DEFAULT_LOCAL_FONT).then((blob) => {
      if (!blob) defaultFont = null      // not granted yet: try again next time
      return blob
    })
  }
  return defaultFont
}

const normalise = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '')

const familyKey = (family: string) => normalise(family).replace(/(?:psmt|mt|ps)$/, '')

const STYLE_ORDER = [
  'thin', 'extralight', 'light', 'semilight', 'medium', 'semibold',
  'bold', 'extrabold', 'black', 'condensed', 'italic',
]
const STYLE_ALIASES: [RegExp, string][] = [
  [/oblique/g, 'italic'],
  [/demibold/g, 'semibold'],
  [/heavy/g, 'black'],
  [/narrow/g, 'condensed'],
  [/regular|normal|book|roman|plain/g, ''],
]

function styleKey(style: string): string {
  let text = normalise(style)
  for (const [pattern, replacement] of STYLE_ALIASES) text = text.replace(pattern, replacement)
  const found: string[] = []
  // Longest first, consuming as we go, so "semibold" is not also "bold".
  for (const word of [...STYLE_ORDER].sort((a, b) => b.length - a.length)) {
    if (text.includes(word)) {
      found.push(word)
      text = text.replace(word, ' ')
    }
  }
  return found.sort((a, b) => STYLE_ORDER.indexOf(a) - STYLE_ORDER.indexOf(b)).join(' ')
}

/**
 * The matching font file, or null if there is none, the browser lacks the
 * API, or the user declines. Must be called from a user gesture the first
 * time, when the browser asks for permission.
 */
export async function findLocalFont(wanted: LocalFontWanted): Promise<Blob | null> {
  if (!localFontsSupported()) return null
  try {
    let fonts = await window.queryLocalFonts!({ postscriptNames: wanted.postscriptNames })
    if (!fonts.length) {
      const all = await window.queryLocalFonts!()
      fonts = all.filter(
        (f) => familyKey(f.family) === wanted.family && styleKey(f.style) === wanted.style,
      )
    }
    return fonts.length ? await fonts[0].blob() : null
  } catch {
    // Permission denied, or not granted in this context. The edit still
    // goes ahead with the best source the server has.
    return null
  }
}
